<!----------------- BEGIN TS ---------------------->
<script lang="ts">
import { initPopover } from "@/plugins/bootstrapSetup";
import { Popover } from "bootstrap";
import { MapZoomLevels } from "@/types";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component({})
export default class TheGoogleMapToggleButton extends Vue {
  // ---------- Props ----------

  /** The zoom level of the map (or off) */
  @Prop() mapZoomLevel!: MapZoomLevels;

  /** Show the map only if the lat and lng is not 0 and all fields exist. */
  @Prop() showMap!: boolean;

  // ------- Local Vars --------

  /** Reference to all popovers so we can modify them (Note that in this component, there is only one). */
  popovers!: Popover[];

  // --------- Watchers --------

  @Watch("mapZoomLevel")
  setDynamicHelpMessage(): void {
    // Need to essentially delete and rebuild because otherwise popover content doesn't change
    if (this.popovers) {
      // NOTE: there is only one, but this is for robustness
      this.popovers.forEach((popover) => {
        popover.dispose();
      });
      this.popovers = initPopover(
        this.$refs.googleMapButton as Element,
        this.dynamicHelpMessage
      );
    }
  }
  // ------- Lifecycle ---------

  mounted(): void {
    if (this.$refs.googleMapButton) {
      this.popovers = initPopover(
        this.$refs.googleMapButton as Element,
        this.dynamicHelpMessage
      );
    }
  }
  // --------- Methods ---------

  /** Choose an icon based on the state. */
  get chooseIcon(): string[] {
    switch (this.mapZoomLevel) {
      case MapZoomLevels.CLOSE:
        return ["compass"];
      case MapZoomLevels.FAR:
        return ["map", "slash"];
      default:
        return ["geo-alt"];
    }
  }

  /** Gets the dynamic help message. */
  get dynamicHelpMessage(): string {
    switch (this.mapZoomLevel) {
      case MapZoomLevels.CLOSE:
        return "Show more context.";
      case MapZoomLevels.FAR:
        return "Hide the map.";
      default:
        return "Show camera location on a map.";
    }
  }
}
</script>
<!----------------- END TS ------------------------>

<!----------------- BEGIN HTML -------------------->
<template lang="html">
  <div
    class="the-google-map-toggle-button-container"
    v-if="showMap"
    ref="googleMapButton"
  >
    <button
      type="button"
      class="btn btn-light map-toggle-button text-dark"
      @click="$emit('update-map-state')"
      data-bs-toggle="popover"
      data-bs-trigger="hover"
      data-bs-container="#app-container"
      data-bs-html="true"
      :data-bs-content="`<span>${dynamicHelpMessage}</span>`"
    >
      <b-iconstack class="icon" font-scale="2">
        <b-icon
          stacked
          v-for="(icon, index) in chooseIcon"
          :key="`icon-${index}`"
          :icon="icon"
          :scale="index > 0 ? '2.5' : '.85'"
        ></b-icon>
      </b-iconstack>
    </button>
  </div>
</template>
<!----------------- END HTML ---------------------->

<!----------------- BEGIN SCSS -------------------->
<style scoped lang="scss">
// Override SCSS Classes here
.the-google-map-toggle-button-container {
  display: flex;

  .map-toggle-button {
    box-shadow: 4px 7px 4px 0px rgb(0 0 0 / 25%);
    border-radius: 0px 0px 10px 10px;
    cursor: pointer;
    font-size: 14px;
    max-height: 71px;

    .icon {
      width: 30px;
      @media only screen and (max-width: 600px),
        screen and (max-height: 550px) {
        width: 20px;
      }

      @media only screen and (max-width: 500px),
        screen and (max-height: 400px) {
        width: 10px;
      }
    }
  }
}
</style>
<!----------------- END SCSS ---------------------->
